import { useMemo, useCallback } from "react";
import { useACLContext } from "../context";
import type { Role, RoleName, Scope } from "@shield-acl/core";

/** Roles concedidas ao usuário no scope (com override opcional). */
export function useGrantedRoles(opts?: { scope?: Scope }): RoleName[] {
  const { engine, user, scope, revision } = useACLContext();
  const active = opts?.scope ?? scope;
  return useMemo(
    () => (user ? engine.getGrantedRoles(user, active) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [engine, user, active, revision],
  );
}

export interface RoleHierarchy {
  allRoles: RoleName[];
  directRoles: RoleName[];
  inheritedRoles: RoleName[];
  hasRole: (name: RoleName) => boolean;
  hasAnyRole: (names: RoleName[]) => boolean;
  hasAllRoles: (names: RoleName[]) => boolean;
  getRole: (name: RoleName) => Role | undefined;
}

/** Hierarquia de roles do usuário no scope (diretas + herdadas). */
export function useRoleHierarchy(opts?: { scope?: Scope }): RoleHierarchy {
  const { engine, user, scope, revision } = useACLContext();
  const active = opts?.scope ?? scope;

  const { allRoles, directRoles, inheritedRoles } = useMemo(() => {
    const direct = user ? engine.getGrantedRoles(user, active) : [];
    const all = new Set<RoleName>(direct);
    const walk = (name: RoleName, seen = new Set<RoleName>()) => {
      if (seen.has(name)) return;
      seen.add(name);
      engine.getRole(name)?.inherits?.forEach((inherited) => {
        all.add(inherited);
        walk(inherited, seen);
      });
    };
    direct.forEach((d) => walk(d));
    const allArr = [...all];
    return {
      allRoles: allArr,
      directRoles: direct,
      inheritedRoles: allArr.filter((r) => !direct.includes(r)),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine, user, active, revision]);

  const hasRole = useCallback(
    (n: RoleName) => allRoles.includes(n),
    [allRoles],
  );
  const hasAnyRole = useCallback(
    (ns: RoleName[]) => ns.some((n) => allRoles.includes(n)),
    [allRoles],
  );
  const hasAllRoles = useCallback(
    (ns: RoleName[]) => ns.every((n) => allRoles.includes(n)),
    [allRoles],
  );
  const getRole = useCallback(
    (n: RoleName) => engine.getRole(n),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [engine, revision],
  );

  return {
    allRoles,
    directRoles,
    inheritedRoles,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    getRole,
  };
}
